import React, { useCallback, useEffect, useState } from 'react';
import queueService from '../../services/queueService';
import { QUEUE_STAGES } from '../../utils/queueLogic';
import { cleanPrefixInput, displayTokenStatusLabel, pairingUrl, validateQueueConfig } from '../../utils/queueDisplay';

const PREFIX_FIELDS = {
  REGISTRATION: 'prefixRegistration', BILLING: 'prefixBilling', COLLECTION: 'prefixCollection', REPORT: 'prefixReport',
};
const errorText = (err, fallback) => err?.response?.data?.message || fallback;
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

/** The one moment a new display token can be read: the link to open on the TV. */
const NewTokenBox = ({ issued, onDone }) => {
  const [copied, setCopied] = useState(false);
  const url = pairingUrl(window.location.origin, issued.token);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); } catch (e) { setCopied(false); }
  };
  return (
    <div className="que-notice" role="alert">
      <p><strong>Open this link on the TV.</strong> It is shown only now and cannot be shown again. If it is lost, issue a new one.</p>
      <input className="que-input" readOnly aria-label="Display link" value={url} onFocus={(e) => e.target.select()} />
      <div className="que-form-row">
        <button type="button" className="que-btn que-btn-primary que-btn-sm" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
        <button type="button" className="que-btn que-btn-sm" onClick={onDone}>I have opened it</button>
      </div>
    </div>
  );
};

/**
 * Queue settings of one centre, for an Administrator or Centre Admin: the prefix of each stage, the automatic COLLECTION token (off until
 * switched on), and the display screens (read-only tokens for the waiting-room TVs).
 */
export const QueueSettingsModal = ({ onClose }) => {
  const [cfg, setCfg] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadTokens = useCallback(async () => {
    try {
      const res = await queueService.displayTokens();
      setTokens(res?.success ? res.data || [] : []);
    } catch (err) { setError(errorText(err, 'Could not load the display screens.')); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await queueService.config();
        if (!cancelled) { if (res?.success) setCfg(res.data); else setError(res?.message || 'Could not load the settings.'); }
      } catch (err) { if (!cancelled) setError(errorText(err, 'Could not load the settings.')); }
    })();
    loadTokens();
    return () => { cancelled = true; };
  }, [loadTokens]);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const save = async () => {
    const problem = validateQueueConfig(cfg);
    if (problem) { setError(problem); return; }
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await queueService.saveConfig({ ...cfg, displayTokenDays: Number(cfg.displayTokenDays) });
      if (res?.success) { setCfg(res.data); setNotice('Settings saved. Tokens already issued keep their labels.'); } else setError(res?.message || 'Could not save.');
    } catch (err) { setError(errorText(err, 'Could not save.')); } finally { setBusy(false); }
  };

  const issue = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await queueService.issueDisplayToken({ label: label.trim() });
      if (res?.success) { setIssued(res.data); setLabel(''); await loadTokens(); } else setError(res?.message || 'Could not issue the display token.');
    } catch (err) { setError(errorText(err, 'Could not issue the display token.')); } finally { setBusy(false); }
  };

  const revoke = async (t) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await queueService.revokeDisplayToken(t.displayTokenId);
      if (res?.success) { setNotice(`“${t.label}” was revoked: that screen stops at its next refresh.`); await loadTokens(); } else setError(res?.message || 'Could not revoke.');
    } catch (err) { setError(errorText(err, 'Could not revoke.')); } finally { setBusy(false); }
  };

  const rotate = async (t) => {
    setBusy(true); setError(''); setNotice('');
    try {
      const res = await queueService.rotateDisplayToken(t.displayTokenId);
      if (res?.success) { setIssued(res.data); await loadTokens(); } else setError(res?.message || 'Could not rotate.');
    } catch (err) { setError(errorText(err, 'Could not rotate.')); } finally { setBusy(false); }
  };

  return (
    <div className="que-modal-overlay" role="dialog" aria-modal="true" aria-label="Queue settings" onClick={onClose}>
      <div className="que-modal" onClick={(e) => e.stopPropagation()}>
        <div className="que-modal-head"><h2>Queue settings</h2><button type="button" className="que-icon-btn" aria-label="Close" onClick={onClose}>×</button></div>
        <div className="que-modal-body">
          {error && <p className="que-error" role="alert">{error}</p>}
          {notice && <p className="que-muted" role="status">{notice}</p>}
          {!cfg ? <p className="que-muted">Loading…</p> : (
            <>
              <h3>Token numbering</h3>
              <p className="que-muted">Each stage prints its own letters before the number, for example C-014. Changing a prefix affects new tokens only.</p>
              <div className="que-form-row">
                {QUEUE_STAGES.map((s) => (
                  <label key={s.code} className="que-inline-label">{s.label}
                    <input className="que-input que-input-sm" style={{ width: 70, textTransform: 'uppercase' }} maxLength={3} aria-label={`${s.label} prefix`}
                      value={cfg[PREFIX_FIELDS[s.code]] || ''} onChange={(e) => set({ [PREFIX_FIELDS[s.code]]: cleanPrefixInput(e.target.value) })} />
                  </label>
                ))}
              </div>

              <h3>Automatic token</h3>
              <label className="que-inline-label">
                <input type="checkbox" checked={!!cfg.autoTokenEnabled} onChange={(e) => set({ autoTokenEnabled: e.target.checked })} />
                {' '}Give a COLLECTION token automatically after a Center Visit registration
              </label>
              <p className="que-muted">Off by default. Home collections and B2B orders never get one. If the token cannot be issued, the registration is still saved and the desk shows it for you to issue.</p>

              <h3>Display screens</h3>
              <label className="que-inline-label">A display token stays valid for
                <input type="number" min="1" max="365" className="que-input que-input-sm" style={{ width: 80 }} aria-label="Display token validity in days"
                  value={cfg.displayTokenDays} onChange={(e) => set({ displayTokenDays: e.target.value })} /> days
              </label>
              <div className="que-form-row">
                <button type="button" className="que-btn que-btn-primary" disabled={busy} onClick={save}>Save settings</button>
              </div>
            </>
          )}

          <h3>Waiting-room screens</h3>
          <p className="que-muted">A screen shows token numbers only and needs no staff login. Revoke a token to switch that screen off at once.</p>
          {issued && <NewTokenBox issued={issued} onDone={() => setIssued(null)} />}
          <div className="que-form-row">
            <input className="que-input" maxLength={60} placeholder="Name of the screen, e.g. Reception TV" aria-label="Screen name" value={label} onChange={(e) => setLabel(e.target.value)} />
            <button type="button" className="que-btn que-btn-primary" disabled={busy || !label.trim()} onClick={issue}>Add screen</button>
          </div>
          {tokens.length === 0 ? <p className="que-muted">No screens paired yet.</p> : (
            <table className="que-table">
              <thead><tr><th>Screen</th><th>Status</th><th>Valid until</th><th>Last used</th><th /></tr></thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.displayTokenId}>
                    <td>{t.label}</td>
                    <td>{displayTokenStatusLabel(t.status)}</td>
                    <td>{when(t.expiresAt)}</td>
                    <td>{when(t.lastUsedAt)}</td>
                    <td className="que-right">
                      <button type="button" className="que-btn que-btn-sm" disabled={busy} onClick={() => rotate(t)} aria-label={`Replace the token of ${t.label}`}>Replace</button>
                      {t.status === 'ACTIVE' && <button type="button" className="que-btn que-btn-sm" disabled={busy} onClick={() => revoke(t)} aria-label={`Revoke ${t.label}`}>Revoke</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueueSettingsModal;
